const clone = (value) => JSON.parse(JSON.stringify(value));

export const createScenarioUuid = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (value) => {
    const random = Math.floor(Math.random() * 16);
    const nibble = value === "x" ? random : (random & 0x3) | 0x8;
    return nibble.toString(16);
  });
};

const sortedByName = (items, key = "name") =>
  [...items].sort((left, right) =>
    String(left?.[key] || "").localeCompare(String(right?.[key] || ""))
  );

export const normalizeScenarioSnapshotForEditor = (rawSnapshot) => {
  const snapshot = clone(rawSnapshot || {});
  snapshot.schema_version = 3;
  if (!Array.isArray(snapshot.standards)) snapshot.standards = [];
  if (!Array.isArray(snapshot.sales_driver_rules)) {
    const definitions = new Map();
    snapshot.standards.forEach((standard) => {
      const driver = standard?.driver;
      if (driver?.type !== "sales" || !driver.uuid) return;
      definitions.set(String(driver.uuid), {
        driver_uuid: driver.uuid,
        driver_name: driver.name,
        ...(driver.sales || {}),
      });
    });
    snapshot.sales_driver_rules = Array.from(definitions.values());
  }
  if (!Array.isArray(snapshot.routed_sales_rules)) {
    const definitions = new Map();
    snapshot.standards.forEach((standard) => {
      const driver = standard?.driver;
      if (driver?.type !== "routed_sales" || !driver.uuid) return;
      definitions.set(String(driver.uuid), {
        driver_uuid: driver.uuid,
        driver_name: driver.name,
        ...(driver.routed_sales || {}),
      });
    });
    snapshot.routed_sales_rules = Array.from(definitions.values());
  }
  if (!Array.isArray(snapshot.attribute_driver_rules)) {
    const definitions = new Map();
    snapshot.standards.forEach((standard) => {
      const driver = standard?.driver;
      if (driver?.type !== "attribute" || !driver.uuid) return;
      definitions.set(String(driver.uuid), {
        driver_uuid: driver.uuid,
        driver_name: driver.name,
        company_value: driver.attribute?.company_value,
        store_values: driver.attribute?.store_values || {},
      });
    });
    snapshot.attribute_driver_rules = Array.from(definitions.values());
  }
  if (!Array.isArray(snapshot.time_windows)) {
    const definitions = new Map();
    snapshot.standards.forEach((standard) => {
      if (standard?.time_window?.uuid) {
        definitions.set(
          String(standard.time_window.uuid),
          clone(standard.time_window)
        );
      }
    });
    snapshot.time_windows = Array.from(definitions.values());
  }
  if (!Array.isArray(snapshot.program_flags)) {
    const definitions = new Map();
    snapshot.standards.forEach((standard) => {
      if (standard?.program_flag?.uuid) {
        definitions.set(
          String(standard.program_flag.uuid),
          clone(standard.program_flag)
        );
      }
    });
    snapshot.program_flags = Array.from(definitions.values());
  }
  if (!Array.isArray(snapshot.location_adjustments)) {
    snapshot.location_adjustments = [];
  }
  return syncScenarioStandardDependencies(snapshot);
};

export const syncScenarioStandardDependencies = (rawSnapshot) => {
  const snapshot = clone(rawSnapshot);
  const timeWindows = new Map(
    snapshot.time_windows.map((item) => [String(item.uuid), item])
  );
  const programFlags = new Map(
    snapshot.program_flags.map((item) => [String(item.uuid), item])
  );
  const drivers = new Map();
  snapshot.sales_driver_rules.forEach((rule) => {
    drivers.set(String(rule.driver_uuid), {
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      type: "sales",
      sales: {
        filter_column: rule.filter_column,
        filter_values: rule.filter_values || [],
        target_column: rule.target_column,
        aggregate_type: rule.aggregate_type,
        is_batch_guidance_flag: Boolean(rule.is_batch_guidance_flag),
        order_index: rule.order_index ?? null,
      },
    });
  });
  snapshot.routed_sales_rules.forEach((rule) => {
    drivers.set(String(rule.driver_uuid), {
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      type: "routed_sales",
      routed_sales: {
        target_store_id: rule.target_store_id,
        source_metric_name: rule.source_metric_name,
        source_store_ids: rule.source_store_ids || [],
        include_target_store_sales:
          rule.include_target_store_sales !== false,
        source_date_offset_days: Number(rule.source_date_offset_days || 0),
        is_active: rule.is_active !== false,
      },
    });
  });
  snapshot.attribute_driver_rules.forEach((rule) => {
    drivers.set(String(rule.driver_uuid), {
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      type: "attribute",
      attribute: {
        company_value: rule.company_value,
        store_values: rule.store_values || {},
      },
    });
  });

  snapshot.standards = snapshot.standards.map((standard) => {
    const next = { ...standard };
    const timeWindow = timeWindows.get(String(standard.time_window?.uuid));
    const driver = drivers.get(String(standard.driver?.uuid));
    const programFlag = programFlags.get(String(standard.program_flag?.uuid));
    if (timeWindow) next.time_window = clone(timeWindow);
    if (standard.driver && driver) next.driver = clone(driver);
    if (standard.program_flag && programFlag) {
      next.program_flag = clone(programFlag);
    }
    return next;
  });
  return snapshot;
};

export const scenarioSnapshotToEditorData = (rawSnapshot, stores = []) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const storesById = new Map(stores.map((store) => [String(store.id), store]));
  const laborDrivers = [];

  snapshot.sales_driver_rules.forEach((rule) => {
    laborDrivers.push({
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      labor_driver_sales: {
        uuid: rule.driver_uuid,
        filter_column: rule.filter_column || "",
        filter_values: rule.filter_values || [],
        filter_values_str: (rule.filter_values || []).join(","),
        target_column: rule.target_column,
        aggregate_type: rule.aggregate_type,
      },
      labor_driver_routed_sales: null,
      labor_driver_attribute: null,
    });
  });
  snapshot.routed_sales_rules.forEach((rule) => {
    laborDrivers.push({
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      labor_driver_sales: null,
      labor_driver_attribute: null,
      labor_driver_routed_sales: {
        uuid: rule.driver_uuid,
        target_store: rule.target_store_id,
        target_store_name:
          storesById.get(String(rule.target_store_id))?.store_name || "",
        source_metric_name: rule.source_metric_name,
        source_stores: (rule.source_store_ids || []).map((storeId) => ({
          id: storeId,
          store_name: storesById.get(String(storeId))?.store_name || `Store ${storeId}`,
        })),
        include_target_store_sales:
          rule.include_target_store_sales !== false,
        source_date_offset_days: Number(rule.source_date_offset_days || 0),
        is_active: rule.is_active !== false,
      },
    });
  });
  snapshot.attribute_driver_rules.forEach((rule) => {
    laborDrivers.push({
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      labor_driver_sales: null,
      labor_driver_routed_sales: null,
      labor_driver_attribute: {
        uuid: rule.driver_uuid,
        value: rule.company_value,
        store_labor_driver_attributes: Object.entries(
          rule.store_values || {}
        ).map(([storeId, value]) => ({ store_id: Number(storeId), value })),
      },
    });
  });

  const laborStandards = snapshot.standards.map((standard) => ({
    uuid: standard.source_uuid,
    type: standard.type,
    name: standard.name,
    department: standard.department,
    role_ids: standard.role_ids || [],
    time_window: {
      ...standard.time_window,
      store_time_windows: [],
    },
    week_days: standard.week_days || [],
    constant_time_minutes: standard.constant_time_minutes,
    scalable_time_minutes: standard.scalable_time_minutes,
    staffing_people_count: standard.staffing_people_count,
    is_minimum_staffing: Boolean(standard.is_minimum_staffing),
    is_maximum_staffing: Boolean(standard.is_maximum_staffing),
    labor_driver: laborDrivers.find(
      (driver) => String(driver.uuid) === String(standard.driver?.uuid)
    ) || null,
    labor_program_flag_company: standard.program_flag
      ? {
          uuid: standard.program_flag.uuid,
          name: standard.program_flag.name,
          default: standard.program_flag.default,
        }
      : null,
    has_changed: false,
    is_new: false,
  }));

  const companyTimeWindows = sortedByName(
    snapshot.time_windows.map((item) => ({
      ...item,
      store_time_windows: [],
    }))
  );
  const laborProgramFlags = sortedByName(
    snapshot.program_flags.map((item) => ({
      uuid: item.uuid,
      name: item.name,
      default: item.default,
    }))
  );
  const laborDriverStoreAttributes = sortedByName(
    snapshot.attribute_driver_rules.map((rule) => ({
      uuid: rule.driver_uuid,
      name: rule.driver_name,
      value: rule.company_value,
      labor_driver_attributes_all_stores: stores.map((store) => {
        const storeKey = String(store.id);
        const isOverride = Object.prototype.hasOwnProperty.call(
          rule.store_values || {},
          storeKey
        );
        return {
          store,
          value: isOverride
            ? rule.store_values[storeKey]
            : rule.company_value,
          is_override: isOverride,
        };
      }),
    }))
  );
  const laborProgramFlagsStoreOverrides = sortedByName(
    snapshot.program_flags.map((flag) => ({
      uuid: flag.uuid,
      name: flag.name,
      default: flag.default,
      all_stores_with_labor_program_flags: stores.map((store) => {
        const storeKey = String(store.id);
        const isOverride = Object.prototype.hasOwnProperty.call(
          flag.store_overrides || {},
          storeKey
        );
        return {
          store,
          override: isOverride
            ? flag.store_overrides[storeKey]
            : flag.default,
          is_override: isOverride,
          store_labor_program_flag_uuid: null,
        };
      }),
    }))
  );
  const adjustmentsByStore = new Map(
    snapshot.location_adjustments.map((item) => [String(item.store_id), item])
  );
  const storesWithAdjustments = stores.map((store) => ({
    ...store,
    ...(adjustmentsByStore.get(String(store.id)) || {}),
  }));

  return {
    snapshot,
    laborStandards,
    laborDrivers: sortedByName(laborDrivers),
    companyTimeWindows,
    laborProgramFlags,
    laborDriverStoreAttributes,
    laborProgramFlagsStoreOverrides,
    storesWithAdjustments,
  };
};

const replaceByIdentity = (items, identityKey, identityValue, replacement) => {
  const index = items.findIndex(
    (item) => String(item[identityKey]) === String(identityValue)
  );
  if (index === -1) return [...items, replacement];
  return items.map((item, itemIndex) =>
    itemIndex === index ? replacement : item
  );
};

export const upsertScenarioTimeWindow = (rawSnapshot, values) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const uuid = values.uuid || createScenarioUuid();
  const existing = snapshot.time_windows.find(
    (item) => String(item.uuid) === String(uuid)
  );
  const replacement = {
    uuid,
    name: String(values.name || "").trim(),
    dynamic_start_time: values.dynamic_start_time,
    dynamic_end_time: values.dynamic_end_time,
    fixed_start_offset_qhnum: Number(values.fixed_start_offset_qhnum),
    fixed_end_offset_qhnum: Number(values.fixed_end_offset_qhnum),
    store_overrides: existing?.store_overrides || {},
  };
  snapshot.time_windows = replaceByIdentity(
    snapshot.time_windows,
    "uuid",
    uuid,
    replacement
  );
  return { snapshot: syncScenarioStandardDependencies(snapshot), timeWindow: replacement };
};

export const deleteScenarioTimeWindow = (rawSnapshot, uuid) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  if (
    snapshot.standards.some(
      (standard) => String(standard.time_window?.uuid) === String(uuid)
    )
  ) {
    throw new Error("This time window is used by a labor standard.");
  }
  snapshot.time_windows = snapshot.time_windows.filter(
    (item) => String(item.uuid) !== String(uuid)
  );
  return snapshot;
};

export const upsertScenarioStandard = (rawSnapshot, values) => {
  let snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  let timeWindowUuid = values.company_time_window_uuid;
  if (!timeWindowUuid) {
    const result = upsertScenarioTimeWindow(snapshot, {
      name: values.company_time_window_name,
      dynamic_start_time: values.dynamic_start_time,
      dynamic_end_time: values.dynamic_end_time,
      fixed_start_offset_qhnum: values.fixed_start_offset_qhnum,
      fixed_end_offset_qhnum: values.fixed_end_offset_qhnum,
    });
    snapshot = result.snapshot;
    timeWindowUuid = result.timeWindow.uuid;
  }
  const timeWindow = snapshot.time_windows.find(
    (item) => String(item.uuid) === String(timeWindowUuid)
  );
  if (!timeWindow) throw new Error("Choose a valid time window.");
  const driver = scenarioSnapshotToEditorData(snapshot).laborDrivers.find(
    (item) => String(item.uuid) === String(values.labor_driver_uuid)
  );
  const programFlag = snapshot.program_flags.find(
    (item) => String(item.uuid) === String(values.program_flag_uuid)
  );
  const sourceUuid = values.uuid || createScenarioUuid();
  const standard = {
    source_uuid: sourceUuid,
    type: values.type,
    name: String(values.name || "").trim(),
    department: {
      id: Number(values.department_id),
      role_type: values.department_name || "Pending validation",
    },
    role_ids: values.role_ids || [],
    time_window: clone(timeWindow),
    week_days: values.week_days || [],
    constant_time_minutes: Number(values.constant_time_minutes || 0),
    scalable_time_minutes: Number(values.scalable_time_minutes || 0),
    staffing_people_count:
      values.staffing_people_count === null ||
      values.staffing_people_count === undefined
        ? null
        : Number(values.staffing_people_count),
    is_minimum_staffing: Boolean(values.is_minimum_staffing),
    is_maximum_staffing: Boolean(values.is_maximum_staffing),
    driver: values.labor_driver_uuid
      ? clone(
          snapshot.standards
            .map((item) => item.driver)
            .find(
              (item) => String(item?.uuid) === String(values.labor_driver_uuid)
            ) ||
            (driver
              ? {
                  uuid: driver.uuid,
                  name: driver.name,
                  type: driver.labor_driver_sales
                    ? "sales"
                    : driver.labor_driver_routed_sales
                    ? "routed_sales"
                    : "attribute",
                }
              : null)
        )
      : null,
    program_flag: programFlag ? clone(programFlag) : null,
  };
  snapshot.standards = replaceByIdentity(
    snapshot.standards,
    "source_uuid",
    sourceUuid,
    standard
  );
  return syncScenarioStandardDependencies(snapshot);
};

export const deleteScenarioStandard = (rawSnapshot, uuid) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  snapshot.standards = snapshot.standards.filter(
    (item) => String(item.source_uuid) !== String(uuid)
  );
  return snapshot;
};

export const upsertScenarioSalesDriver = (rawSnapshot, values) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const driverUuid =
    values.labor_driver_uuid || values.labor_driver_sales_uuid || createScenarioUuid();
  const existing = snapshot.sales_driver_rules.find(
    (item) => String(item.driver_uuid) === String(driverUuid)
  );
  const rule = {
    driver_uuid: driverUuid,
    driver_name: String(values.name || "").trim(),
    filter_column: values.filter_column || "",
    filter_values: values.filter_values || [],
    target_column: values.target_column,
    aggregate_type: values.aggregate_type,
    is_batch_guidance_flag: Boolean(existing?.is_batch_guidance_flag),
    order_index: existing?.order_index ?? null,
  };
  snapshot.sales_driver_rules = replaceByIdentity(
    snapshot.sales_driver_rules,
    "driver_uuid",
    driverUuid,
    rule
  );
  return syncScenarioStandardDependencies(snapshot);
};

export const upsertScenarioRoutedDriver = (rawSnapshot, values) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const driverUuid =
    values.labor_driver_uuid ||
    values.labor_driver_routed_sales_uuid ||
    createScenarioUuid();
  const existing = snapshot.routed_sales_rules.find(
    (item) => String(item.driver_uuid) === String(driverUuid)
  );
  const rule = {
    driver_uuid: driverUuid,
    driver_name: String(values.name || "").trim(),
    target_store_id: Number(values.target_store_id),
    source_metric_name: String(values.source_metric_name || "").trim(),
    source_store_ids: (values.source_store_ids || []).map(Number),
    include_target_store_sales:
      values.include_target_store_sales !== false,
    source_date_offset_days: Number(values.source_date_offset_days || 0),
    is_active: values.is_active !== false,
    order_index: existing?.order_index ?? null,
  };
  snapshot.routed_sales_rules = replaceByIdentity(
    snapshot.routed_sales_rules,
    "driver_uuid",
    driverUuid,
    rule
  );
  return syncScenarioStandardDependencies(snapshot);
};

export const upsertScenarioAttributeDriver = (rawSnapshot, values) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const driverUuid = values.uuid || createScenarioUuid();
  const existing = snapshot.attribute_driver_rules.find(
    (item) => String(item.driver_uuid) === String(driverUuid)
  );
  const rule = {
    driver_uuid: driverUuid,
    driver_name: String(values.name || "").trim(),
    company_value: Number(values.value),
    store_values: existing?.store_values || {},
  };
  snapshot.attribute_driver_rules = replaceByIdentity(
    snapshot.attribute_driver_rules,
    "driver_uuid",
    driverUuid,
    rule
  );
  return syncScenarioStandardDependencies(snapshot);
};

export const deleteScenarioDriver = (rawSnapshot, uuid) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  if (
    snapshot.standards.some(
      (standard) => String(standard.driver?.uuid) === String(uuid)
    )
  ) {
    throw new Error("This labor driver is used by a labor standard.");
  }
  ["sales_driver_rules", "routed_sales_rules", "attribute_driver_rules"].forEach(
    (key) => {
      snapshot[key] = snapshot[key].filter(
        (item) => String(item.driver_uuid) !== String(uuid)
      );
    }
  );
  return snapshot;
};

export const upsertScenarioProgramFlag = (rawSnapshot, values) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const uuid = values.uuid || createScenarioUuid();
  const existing = snapshot.program_flags.find(
    (item) => String(item.uuid) === String(uuid)
  );
  const flag = {
    uuid,
    name: String(values.name || "").trim(),
    default: Boolean(values.defaultValue),
    store_overrides: existing?.store_overrides || {},
  };
  snapshot.program_flags = replaceByIdentity(
    snapshot.program_flags,
    "uuid",
    uuid,
    flag
  );
  return syncScenarioStandardDependencies(snapshot);
};

export const deleteScenarioProgramFlag = (rawSnapshot, uuid) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  if (
    snapshot.standards.some(
      (standard) => String(standard.program_flag?.uuid) === String(uuid)
    )
  ) {
    throw new Error("This flag is used by a labor standard.");
  }
  snapshot.program_flags = snapshot.program_flags.filter(
    (item) => String(item.uuid) !== String(uuid)
  );
  return snapshot;
};

export const setScenarioAttributeStoreValue = (
  rawSnapshot,
  driverUuid,
  storeId,
  value
) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const rule = snapshot.attribute_driver_rules.find(
    (item) => String(item.driver_uuid) === String(driverUuid)
  );
  if (!rule) throw new Error("Attribute driver was not found.");
  if (value === null || value === undefined) {
    const nextStoreValues = { ...(rule.store_values || {}) };
    delete nextStoreValues[String(storeId)];
    rule.store_values = nextStoreValues;
    return syncScenarioStandardDependencies(snapshot);
  }
  rule.store_values = {
    ...(rule.store_values || {}),
    [String(storeId)]: Number(value),
  };
  return syncScenarioStandardDependencies(snapshot);
};

export const setScenarioProgramFlagStoreValue = (
  rawSnapshot,
  flagUuid,
  storeId,
  value
) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const flag = snapshot.program_flags.find(
    (item) => String(item.uuid) === String(flagUuid)
  );
  if (!flag) throw new Error("Program flag was not found.");
  if (value === null || value === undefined) {
    const nextStoreOverrides = { ...(flag.store_overrides || {}) };
    delete nextStoreOverrides[String(storeId)];
    flag.store_overrides = nextStoreOverrides;
    return syncScenarioStandardDependencies(snapshot);
  }
  flag.store_overrides = {
    ...(flag.store_overrides || {}),
    [String(storeId)]: Boolean(value),
  };
  return syncScenarioStandardDependencies(snapshot);
};

export const setScenarioLocationAdjustment = (
  rawSnapshot,
  storeId,
  values
) => {
  const snapshot = normalizeScenarioSnapshotForEditor(rawSnapshot);
  const existing = snapshot.location_adjustments.find(
    (item) => String(item.store_id) === String(storeId)
  ) || { store_id: Number(storeId) };
  const adjustment = {
    ...existing,
    ...values,
    store_id: Number(storeId),
  };
  if (!adjustment.labor_budget_mode || adjustment.labor_budget_mode === "bottom_up") {
    adjustment.labor_budget_mode = null;
    adjustment.labor_budget_target_value = null;
  }
  snapshot.location_adjustments = replaceByIdentity(
    snapshot.location_adjustments,
    "store_id",
    Number(storeId),
    adjustment
  );
  return snapshot;
};
